// Seller (listing) file-request → real file. Mirrors buyerFileRequest.js: mints
// a hidden seller file-request deal (Drive folder via the deal trigger), links
// it to the listing lead by sourceDealId, and carries the chosen `formType` so
// the Client Portal assembles the property-type-tailored forms (condo/pool/
// multi-family) instead of the generic seller set. One idempotent source of
// truth so the modal and the drawer can't drift or double-create.
// See docs/superpowers/specs/2026-07-14-pre-deploy-file-folder-improvements-design.md
import {
  createFileRequestFromLead,
  updateListingLead,
} from "./firebasefunctions";
import { emailListingRequestRecipients } from "./helper";
import { listClients } from "./leadClients";

// The Drive folder is named once, from the file's address, at the instant the
// file is created — and nothing renames it afterwards. A file minted with a
// blank address is stuck as "Untitled Deal (id)" forever, so refuse to mint one.
// Seller-only: a buyer file legitimately has no property yet.
export const MISSING_ADDRESS_MESSAGE =
  "Add the property address before requesting the file — it names the Drive folder.";

// emailListingRequestRecipients reads a deal, not a lead. On the re-request
// path there is no fresh deal to hand it, so the lead is presented in the same
// shape — otherwise the email goes out with "(no address provided)" as its
// subject, which is worse than useless to whoever has to act on it.
function dealShapeOf(lead) {
  return {
    createdat: Number(lead?.sourceDealId),
    id: String(lead?.sourceDealId || ""),
    dealtype: "Seller",
    propertyaddress: lead?.propertyAddress || "",
    agentData: lead?.assignedAgent || "",
    clientLastName: lead?.contactName || "",
    customers: listClients(lead)
      .map((c) => c.contactId)
      .filter(Boolean),
    notes: lead?.notes || "",
  };
}

// Idempotent: if the lead already links a file (sourceDealId), don't create a
// second one — just (re)flag it. `formType` chooses the portal form set.
export async function requestSellerFile(
  lead,
  user = {},
  { sendForms = false, formType = "", details = {} } = {},
) {
  if (lead?.sourceDealId) {
    await updateListingLead(lead.id, {
      fileRequested: true,
      fileRequestedAt: Date.now(),
    });
    // Requesting a file is always worth telling Client Care about, including
    // the second time. This branch used to return here, so a file that was
    // requested, deleted and requested again (3015 Colfax, 2026-08-25) notified
    // nobody at all and said nothing about it. The lead still links the old
    // file, so no second file is minted — only the notification is repeated.
    emailListingRequestRecipients(dealShapeOf(lead), user).catch((err) =>
      console.error("Listing request email blast failed:", err),
    );
    return {
      deal: { createdat: Number(lead.sourceDealId), id: String(lead.sourceDealId) },
      reused: true,
      sendForms,
    };
  }

  // Checked AFTER the sourceDealId early return: a lead that already has a file
  // is only being re-flagged, and blocking that would break re-sending forms
  // for an older file.
  if (!String(lead?.propertyAddress || "").trim()) {
    throw new Error(MISSING_ADDRESS_MESSAGE);
  }

  const primary = listClients(lead).find((c) => c.primary);
  const fr = await createFileRequestFromLead(
    {
      contactId: primary?.contactId || lead.contactId || "",
      contactName: lead.contactName || "",
      assignedAgent: lead.assignedAgent || "",
      assignedAgentName: lead.assignedAgentName || "",
      propertyAddress: lead.propertyAddress || "",
      source: lead.source || "",
      notes: lead.notes || "",
      formType: formType || lead.formType || "",
    },
    "seller",
    user,
    // The request dialog's answers (file format, deadline, trust, …) so the
    // team gets a complete request. See utils/fileRequestDetails.js.
    details,
  );
  if (!fr?.success || !fr.deal) {
    throw new Error(fr?.error || "Could not create the listing file");
  }

  const linkRes = await updateListingLead(lead.id, {
    sourceDealId: String(fr.deal.createdat),
    fileRequested: true,
    fileRequestedAt: Date.now(),
  });
  // updateListingLead resolves {success:false} instead of throwing. An unlinked
  // lead would later re-prompt for a second file and graduate a bare deal.
  if (linkRes?.success === false) {
    throw new Error(
      "File created, but it couldn't be tied to the pipeline lead. Open the lead and check before requesting again.",
    );
  }
  // Email the Settings → Notifications request recipients (fire-and-forget).
  emailListingRequestRecipients(fr.deal, user).catch((err) =>
    console.error("Listing request email blast failed:", err),
  );
  return { deal: fr.deal, reused: false, sendForms };
}
